import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, Alert } from '@octano/global-ui';
import { Card } from 'reactstrap';
import { TranslationsKeys } from '../../../../locales/translations';
import { useReportsColumns } from './hooks/useReportsColumns';
import { ReportAsyncGroup, ReportAsyncTypeRow } from './types';
import GenerateReportModal, {
  GenerateReportModalMethods,
} from './parts/GenerateReportModal';
import { getAsyncReports } from './requests';

type ReportAsyncProps = {
  group?: ReportAsyncGroup;
};

const descriptions: Record<ReportAsyncGroup, string> = {
  [ReportAsyncGroup.Coursables]: 'descriptionCoursables',
  [ReportAsyncGroup.SIES]: 'descriptionSIES',
  [ReportAsyncGroup.Default]: 'description',
};

const ReportAsync = ({
  group = ReportAsyncGroup.Default,
}: ReportAsyncProps) => {
  const { t } = useTranslation(TranslationsKeys.MANAGEMENT_TOOLS_REPORTS_ASYNC);
  const generatorRef = useRef<GenerateReportModalMethods>(null);

  const [mounted, setMounted] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [items, setItems] = useState<ReportAsyncTypeRow[]>([]);

  const isGenerating = useMemo(
    () => items?.some((item) => !!item?.generating),
    [items],
  );

  const handlePressGenerate = useCallback((row: ReportAsyncTypeRow) => {
    generatorRef?.current?.open(row);
  }, []);

  const columns = useReportsColumns({ onPressGenerate: handlePressGenerate });

  const fetchData = useCallback(async () => {
    if (loading) {
      return;
    }
    setLoading(true);
    try {
      const { data = [], error } = await getAsyncReports(group);
      if (!error && data?.length) {
        setItems(data);
      } else {
        setItems([]);
      }
    } catch (error) {
      setItems([]);
    }
    setLoading(false);
  }, [loading, group]);

  useEffect(() => {
    if (!mounted) {
      fetchData();
      setMounted(true);
    }
  }, [mounted, fetchData]);

  return (
    <>
      <Card className="p-4">
        <p className="mb-4 mt-2">{t(descriptions[group] || 'default')}</p>
        {!!isGenerating && (
          <Alert
            color="info"
            icon="information"
            noFill
            size="sm"
            text={t(`alerts.isGenerating`)}
            isOpen={true}
          />
        )}
        <Table columns={columns} data={items} isLoadingResults={loading} />
      </Card>
      <GenerateReportModal ref={generatorRef} onGenerate={fetchData} />
    </>
  );
};

export default ReportAsync;
